import { Component, Director, director, Enum, Graphics, Size, UITransform, Vec2, Vec3, _decorator } from 'cc';

const { ccclass, property, executeInEditMode } = _decorator;
const tempPosition = new Vec3();

enum GraphicsCommandType {
    MoveTo,
    LineTo,
    BezierCurveTo,
    QuadraticCurveTo,
    Arc,
    Ellipse,
    Circle,
    Rect,
    RoundRect,
    Close,
    Stroke,
    Fill,
    DrawTransform,
}

interface IGraphicsCommandExecutor {
    execute(graphics: Graphics, command: GraphicsCommand): void;
}

/* #region command executor */
class MoveToCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.moveTo(command.point.x, command.point.y);
    }
}

class LineToCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.lineTo(command.point.x, command.point.y);
    }
}

class BezierCurveToCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.bezierCurveTo(command.point.x, command.point.y, command.secondPoint.x, command.secondPoint.y, command.thirdPoint.x, command.thirdPoint.y);
    }
}

class QuadraticCurveToCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.quadraticCurveTo(command.point.x, command.point.y, command.secondPoint.x, command.secondPoint.y);
    }
}

class ArcCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.arc(command.point.x, command.point.y, command.radius, command.startAngle, command.endAngle, command.counterclockwise);
    }
}

class EllipseCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.ellipse(command.point.x, command.point.y, command.radius, command.radiusY);
    }
}

class CircleCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.circle(command.point.x, command.point.y, command.radius);
    }
}

class RectCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.rect(command.point.x, command.point.y, command.contentSize.width, command.contentSize.height);
    }
}

class RoundRectCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.roundRect(command.point.x, command.point.y, command.contentSize.width, command.contentSize.height, command.radius);
    }
}

class CloseCommandExecutor implements IGraphicsCommandExecutor {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.close();
    }
}

class StrokeCommandExecutor implements IGraphicsCommandExecutor {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.stroke();
    }
}

class FillCommandExecutor implements IGraphicsCommandExecutor {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public execute(graphics: Graphics, command: GraphicsCommand) {
        graphics.fill();
    }
}

class DrawTransformCommandExecutor implements IGraphicsCommandExecutor {
    public execute(graphics: Graphics, command: GraphicsCommand) {
        command.transforms.forEach((transform) => {
            if (!transform || !transform.node || !transform.node.active) {
                return;
            }
            const boundingBox = transform.getBoundingBox();
            tempPosition.set(boundingBox.x, boundingBox.y);
            transform.node.parent.transform.convertToWorldSpaceAR(tempPosition, tempPosition);
            graphics.node.transform.convertToNodeSpaceAR(tempPosition, tempPosition);
            graphics.rect(tempPosition.x, tempPosition.y, boundingBox.width, boundingBox.height);
        });
    }
}
/* #endregion */

@ccclass('GraphicsCommand')
export class GraphicsCommand implements IGraphicsCommandExecutor {
    private static readonly COMMAND_EXECUTOR_MAP: { [commandType in GraphicsCommandType]: IGraphicsCommandExecutor } = {
        [GraphicsCommandType.MoveTo]: new MoveToCommandExecutor(),
        [GraphicsCommandType.LineTo]: new LineToCommandExecutor(),
        [GraphicsCommandType.BezierCurveTo]: new BezierCurveToCommandExecutor(),
        [GraphicsCommandType.QuadraticCurveTo]: new QuadraticCurveToCommandExecutor(),
        [GraphicsCommandType.Arc]: new ArcCommandExecutor(),
        [GraphicsCommandType.Ellipse]: new EllipseCommandExecutor(),
        [GraphicsCommandType.Circle]: new CircleCommandExecutor(),
        [GraphicsCommandType.Rect]: new RectCommandExecutor(),
        [GraphicsCommandType.RoundRect]: new RoundRectCommandExecutor(),
        [GraphicsCommandType.Close]: new CloseCommandExecutor(),
        [GraphicsCommandType.Stroke]: new StrokeCommandExecutor(),
        [GraphicsCommandType.Fill]: new FillCommandExecutor(),
        [GraphicsCommandType.DrawTransform]: new DrawTransformCommandExecutor(),
    };
    private static readonly WATCH_PROPERTIES: (keyof GraphicsCommand)[] = [
        'type',
        'point',
        'secondPoint',
        'thirdPoint',
        'radius',
        'startAngle',
        'endAngle',
        'counterclockwise',
        'radiusY',
        'contentSize',
        'transforms',
    ];

    @property({ type: Enum(GraphicsCommandType) })
    public type: GraphicsCommandType = GraphicsCommandType.MoveTo;

    @property({
        visible() {
            return this.type !== GraphicsCommandType.Stroke && this.type !== GraphicsCommandType.Fill && this.type !== GraphicsCommandType.DrawTransform;
        },
    })
    public point: Vec2 = new Vec2();

    @property({
        visible() {
            return this.type === GraphicsCommandType.BezierCurveTo || this.type === GraphicsCommandType.QuadraticCurveTo;
        },
    })
    public secondPoint: Vec2 = new Vec2();

    @property({
        visible() {
            return this.type === GraphicsCommandType.BezierCurveTo;
        },
    })
    public thirdPoint: Vec2 = new Vec2();

    @property({
        visible() {
            return (
                this.type === GraphicsCommandType.Arc ||
                this.type === GraphicsCommandType.Circle ||
                this.type === GraphicsCommandType.Ellipse ||
                this.type === GraphicsCommandType.RoundRect
            );
        },
    })
    public radius: number = 0;

    @property({
        visible() {
            return this.type === GraphicsCommandType.Arc;
        },
    })
    public startAngle: number = 0;

    @property({
        visible() {
            return this.type === GraphicsCommandType.Arc;
        },
    })
    public endAngle: number = 0;

    @property({
        visible() {
            return this.type === GraphicsCommandType.Arc;
        },
    })
    public counterclockwise: boolean = false;

    @property({
        visible() {
            return this.type === GraphicsCommandType.Ellipse;
        },
    })
    public radiusY: number = 0;

    @property({
        visible() {
            return this.type === GraphicsCommandType.Rect || this.type === GraphicsCommandType.RoundRect;
        },
    })
    public contentSize: Size = new Size();

    @property({
        type: [UITransform],
        visible() {
            return this.type === GraphicsCommandType.DrawTransform;
        },
    })
    public transforms: UITransform[] = [];

    private _dirty = true;

    constructor() {
        // eslint-disable-next-line no-constructor-return
        return new Proxy(this, {
            set(target, p, value, receiver) {
                const setReturn = Reflect.set(target, p, value, receiver);
                if (setReturn) {
                    if (GraphicsCommand.WATCH_PROPERTIES.includes(p as keyof GraphicsCommand)) {
                        target._dirty = true;
                    }
                }
                return setReturn;
            },
        });
    }

    public get dirty() {
        return this._dirty;
    }

    public execute(graphics: Graphics) {
        this._dirty = false;
        GraphicsCommand.COMMAND_EXECUTOR_MAP[this.type].execute(graphics, this);
    }
}

@ccclass('GraphicsPainter')
@executeInEditMode
export default class GraphicsPainter extends Component {
    @property([GraphicsCommand])
    public commands: GraphicsCommand[] = [];

    private _graphics: Graphics = null;

    private _dirty = true;

    constructor() {
        super();
        // eslint-disable-next-line no-constructor-return
        return new Proxy(this, {
            set(target, p, value, receiver) {
                const setReturn = Reflect.set(target, p, value, receiver);
                if (setReturn) {
                    if (p === 'commands') {
                        target._dirty = true;
                    }
                }
                return setReturn;
            },
        });
    }

    @property
    private set doPainting(value: boolean) {
        this.paint();
    }
    private get doPainting() {
        return true;
    }

    private get graphics() {
        if (!this._graphics) {
            this._graphics = this.getComponent(Graphics);
            if (!this._graphics) {
                this._graphics = this.addComponent(Graphics);
            }
        }
        return this._graphics;
    }

    public onEnable() {
        director.on(Director.EVENT_AFTER_DRAW, this._beforeDraw, this);
        this.paint();
    }

    public onDisable() {
        director.off(Director.EVENT_AFTER_DRAW, this._beforeDraw, this);
    }

    public paint() {
        const { graphics } = this;
        graphics.clear();
        this.commands?.forEach((command) => command.execute(graphics));
    }

    private _beforeDraw() {
        if (!this._dirty && this.commands.every((command) => !command.dirty)) {
            return;
        }
        this._dirty = false;
        this.paint();
    }
}
